import { forwardRef } from 'react';

/**
 * Card Component - A versatile container with multiple variants
 * 
 * @param {Object} props - Component props
 * @param {string} props.variant - Card variant: 'default', 'hover', 'bordered', 'flat'
 * @param {string} props.padding - Padding size: 'none', 'sm', 'md', 'lg'
 * @param {boolean} props.loading - Shows loading skeleton when true
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.children - Card content
 */
const Card = forwardRef(({
  variant = 'default',
  padding = 'md',
  loading = false,
  className = '',
  children,
  onClick,
  ...props
}, ref) => {
  // Variant classes
  const variantClasses = {
    default: 'bg-white rounded-lg shadow-card',
    hover: 'bg-white rounded-lg shadow-card hover:shadow-card-hover hover:-translate-y-0.5 cursor-pointer transition-all duration-200',
    bordered: 'bg-white rounded-lg border border-gray-200',
    flat: 'bg-gray-50 rounded-lg',
  };

  // Padding classes
  const paddingClasses = {
    none: '',
    sm: 'p-3',
    md: 'p-4 sm:p-6',
    lg: 'p-6 sm:p-8',
  };

  return (
    <div
      ref={ref}
      onClick={onClick}
      className={`
        ${variantClasses[variant]}
        ${paddingClasses[padding]}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      {...props}
    >
      {loading ? (
        <CardSkeleton />
      ) : (
        children
      )}
    </div>
  );
});

Card.displayName = 'Card';

/**
 * CardHeader Component - Header section of a card
 */
export const CardHeader = ({ children, className = '', action }) => {
  return (
    <div className={`flex items-center justify-between mb-4 ${className}`}>
      <div className="flex-1">{children}</div>
      {action && <div className="ml-4">{action}</div>}
    </div>
  );
};

/**
 * CardTitle Component - Title for a card
 */
export const CardTitle = ({ children, className = '' }) => {
  return (
    <h3 className={`text-lg font-semibold text-gray-900 ${className}`}>
      {children}
    </h3>
  );
};

/**
 * CardDescription Component - Description for a card
 */
export const CardDescription = ({ children, className = '' }) => {
  return (
    <p className={`text-sm text-gray-500 mt-1 ${className}`}>
      {children}
    </p>
  );
};

/**
 * CardContent Component - Main content area of a card
 */
export const CardContent = ({ children, className = '' }) => {
  return (
    <div className={className}>
      {children}
    </div>
  );
};

/**
 * CardFooter Component - Footer section of a card
 */
export const CardFooter = ({ children, className = '' }) => {
  return (
    <div className={`mt-4 pt-4 border-t border-gray-100 ${className}`}>
      {children}
    </div>
  );
};

/**
 * StatCard Component - Card for displaying statistics
 */
export const StatCard = ({
  title,
  value,
  icon: Icon,
  trend,
  trendValue,
  color = 'primary',
  className = '',
}) => {
  const colorClasses = {
    primary: 'bg-primary-100 text-primary-600',
    accent: 'bg-accent-100 text-accent-600',
    success: 'bg-success-light text-success',
    warning: 'bg-warning-light text-warning-dark',
    error: 'bg-error-light text-error',
    info: 'bg-info-light text-info',
  };

  const trendClasses = {
    up: 'text-success',
    down: 'text-error',
    neutral: 'text-gray-500',
  };

  return (
    <Card className={className}>
      <div className="flex items-center space-x-4">
        {Icon && (
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        )}
        <div className="flex-1">
          <p className="text-sm text-gray-500">{title}</p>
          <div className="flex items-baseline space-x-2">
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            {trend && (
              <span className={`text-sm font-medium ${trendClasses[trend]}`}>
                {trend === 'up' ? '+' : trend === 'down' ? '-' : ''}{trendValue}
              </span>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

/**
 * CardSkeleton Component - Loading skeleton for cards
 */
export const CardSkeleton = ({ lines = 3 }) => {
  return (
    <div className="animate-pulse space-y-4">
      <div className="skeleton h-4 w-3/4"></div>
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className={`skeleton h-4 ${i === lines - 1 ? 'w-1/2' : 'w-full'}`}></div>
      ))}
    </div>
  );
};

export default Card;